import { Link } from 'react-router-dom';
import { 
  LogOut, 
  UserPlus, 
  Upload, 
  User,
  Settings,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useLoggedInAccounts, type Account } from '@/hooks/useLoggedInAccounts';
import { useNWC } from '@/hooks/useNWCContext';
import { useStreamingPreferences } from '@/hooks/useStreamingPreferences';
import { genUserName } from '@/lib/genUserName';

interface ProfileDropdownProps {
  onUploadClick: () => void;
  onAddAccountClick: () => void;
}

export function ProfileDropdown({ onUploadClick, onAddAccountClick }: ProfileDropdownProps) {
  const { currentUser, otherUsers, setLogin, removeLogin } = useLoggedInAccounts();
  const { connections } = useNWC();
  const { preferences, getTotalPerMinute } = useStreamingPreferences();
  
  const hasWallet = connections.length > 0 && connections.some(c => c.isConnected);
  const isStreaming = preferences.enabled && getTotalPerMinute() > 0 && hasWallet;

  if (!currentUser) return null;

  const getDisplayName = (account: Account): string => {
    return account.metadata.name ?? genUserName(account.pubkey);
  };

  return (
    <>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <button className="relative flex items-center justify-center rounded-full hover:ring-2 hover:ring-primary/30 transition-all focus:outline-none focus:ring-2 focus:ring-primary/50">
            <Avatar className="h-9 w-9 ring-2 ring-white/10">
              <AvatarImage src={currentUser.metadata.picture} alt={getDisplayName(currentUser)} />
              <AvatarFallback className="bg-gradient-to-br from-primary/20 to-primary/10 text-primary">
                {getDisplayName(currentUser).charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            {/* Online indicator */}
            <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full bg-green-500 ring-2 ring-background" />
          </button>
        </DropdownMenuTrigger>
        
        <DropdownMenuContent 
          align="end" 
          className="w-72 p-0 overflow-hidden glass border-white/10"
          sideOffset={8}
        >
          {/* Profile Header */}
          <div className="p-4 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent">
            <div className="flex items-center gap-3">
              <Avatar className="h-12 w-12 ring-2 ring-white/20">
                <AvatarImage src={currentUser.metadata.picture} alt={getDisplayName(currentUser)} />
                <AvatarFallback className="bg-primary/20 text-primary text-lg">
                  {getDisplayName(currentUser).charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="font-semibold truncate">{getDisplayName(currentUser)}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {currentUser.metadata.nip05 || 'Nostr User'}
                </p>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="p-2">
            <DropdownMenuItem 
              onClick={onUploadClick}
              className="flex items-center gap-3 p-3 rounded-lg cursor-pointer"
            >
              <div className="p-2 rounded-lg bg-white/5">
                <Upload className="h-4 w-4" />
              </div>
              <span>Upload Music</span>
            </DropdownMenuItem>

            <DropdownMenuItem asChild>
              <Link 
                to="/wallet"
                className="flex items-center gap-3 p-3 rounded-lg cursor-pointer"
              >
                <div className="p-2 rounded-lg bg-white/5">
                  <Settings className="h-4 w-4" />
                </div>
                <span>Streaming Settings</span>
                {isStreaming && (
                  <span className="ml-auto text-xs text-orange-400 font-medium">
                    {getTotalPerMinute()}/min
                  </span>
                )}
              </Link>
            </DropdownMenuItem>

            <DropdownMenuItem asChild>
              <Link 
                to="/profile"
                className="flex items-center gap-3 p-3 rounded-lg cursor-pointer"
              >
                <div className="p-2 rounded-lg bg-white/5">
                  <User className="h-4 w-4" />
                </div>
                <span>My Profile</span>
              </Link>
            </DropdownMenuItem>
          </div>

          {/* Other Accounts */}
          {otherUsers.length > 0 && (
            <>
              <DropdownMenuSeparator className="bg-white/5" />
              <div className="p-2">
                <p className="px-3 py-1.5 text-xs text-muted-foreground font-medium">
                  Switch Account
                </p>
                {otherUsers.map((user) => (
                  <DropdownMenuItem
                    key={user.id}
                    onClick={() => setLogin(user.id)}
                    className="flex items-center gap-3 p-2 rounded-lg cursor-pointer"
                  >
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={user.metadata.picture} alt={getDisplayName(user)} />
                      <AvatarFallback className="text-xs">
                        {getDisplayName(user).charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-sm truncate">{getDisplayName(user)}</span>
                  </DropdownMenuItem>
                ))}
              </div>
            </>
          )}

          <DropdownMenuSeparator className="bg-white/5" />
          
          {/* Footer Actions */}
          <div className="p-2">
            <DropdownMenuItem
              onClick={onAddAccountClick}
              className="flex items-center gap-3 p-3 rounded-lg cursor-pointer"
            >
              <UserPlus className="h-4 w-4" />
              <span>Add Account</span>
            </DropdownMenuItem>
            
            <DropdownMenuItem
              onClick={() => removeLogin(currentUser.id)}
              className="flex items-center gap-3 p-3 rounded-lg cursor-pointer text-red-400 focus:text-red-400"
            >
              <LogOut className="h-4 w-4" />
              <span>Log Out</span>
            </DropdownMenuItem>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}
